import { isImpersonated } from "@/hooks/api/auth/tokenManager";
import { desmarcarFeriadoTrabajo, getFeriadosTrabaja, marcarFeriadoTrabajo } from "@/hooks/api/feriados";

/**
 * "Voy a trabajar" de la psicóloga frente a un feriado, compartido por todo lo que lo muestra
 * (aviso de la Agenda, aviso de Comunidad y la marca del feriado en la cabecera de cada día):
 * cuando cambia en un lado, el resto se entera por `FERIADOS_CHANGED_EVENT` y vuelve a leer.
 *
 * Impersonando no se guarda en el servidor (soporte no le cambia la decisión a la psicóloga):
 * el cambio vale solo en esta pestaña, para poder probarlo.
 */
export const FERIADOS_CHANGED_EVENT = "psi-feriados-changed";

/** Cambios hechos en esta pestaña que pisan lo que dice el servidor. */
const overrides = new Map<string, boolean>();

/** Una consulta compartida entre todos los que la piden a la vez (cada día de la Agenda tiene
 *  su marca); se descarta al cambiar algo o a los 30 s. */
let pending: { at: number; promise: Promise<string[]> } | null = null;

/** Feriados (YYYY-MM-DD) en los que dijo "Voy a trabajar". Si falla la consulta, ninguno. */
export async function loadFeriadosTrabaja(): Promise<Set<string>> {
  if (!pending || Date.now() - pending.at > 30_000) {
    pending = { at: Date.now(), promise: getFeriadosTrabaja().catch(() => [] as string[]) };
  }
  const server = await pending.promise;
  const set = new Set(server);
  overrides.forEach((trabaja, date) => {
    if (trabaja) set.add(date);
    else set.delete(date);
  });
  return set;
}

/** Marca o desmarca "Voy a trabajar" y avisa al resto de la pantalla. */
export async function setFeriadoTrabaja(date: string, trabaja: boolean): Promise<void> {
  overrides.set(date, trabaja);
  pending = null;
  window.dispatchEvent(new CustomEvent(FERIADOS_CHANGED_EVENT, { detail: { date, trabaja } }));
  if (isImpersonated()) return;
  await (trabaja ? marcarFeriadoTrabajo(date) : desmarcarFeriadoTrabajo(date)).catch(() => undefined);
}
